// JS wrapper for the native production Notification Center (Android only).
// See android/app/src/main/java/chat/livora/app/notifications/ for the
// native implementation. Native FirebaseAuth session bootstrap/clear
// lives in NativeFirebaseSession.js instead - it is generic and shared by
// every progressively-migrated native page, not owned by this screen.

import { Capacitor } from "@capacitor/core";

function getNativeNotificationCenterPlugin() {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    return window.Capacitor?.Plugins?.NativeNotificationCenter ?? null;
  } catch {
    return null;
  }
}

export function isNativeNotificationCenterAvailable() {
  return Capacitor.getPlatform() === "android" && !!getNativeNotificationCenterPlugin();
}

/**
 * Closed-set validation mirroring the native side's own
 * NativeThemeIdentifier.validate exactly - only "male"/"female" pass
 * through, everything else (including "dark"/"light", which a separate,
 * unrelated light/dark-mode toggle - src/theme.js's applyTheme - can also
 * write to the same document.documentElement data-theme attribute) safely
 * becomes "default". Exported so callers/tests can reuse the exact same
 * rule rather than re-implementing it.
 */
export function normalizeNativeThemeIdentifier(raw) {
  return raw === "male" || raw === "female" ? raw : "default";
}

/**
 * Opens the native Notification Center Activity. Android only.
 *
 * @param {string} [themeIdentifier] the caller's already-resolved active
 *     theme (see BottomNav.js: document.documentElement's data-theme
 *     attribute, set by ThemeBridge in App.js from users/{uid}.gender - no
 *     new Firestore read here). Normalized through
 *     normalizeNativeThemeIdentifier before being sent; native
 *     independently re-validates it again on arrival regardless.
 */
export async function openNativeNotificationCenter(themeIdentifier) {
  const plugin = getNativeNotificationCenterPlugin();
  if (!plugin || typeof plugin.openNotificationCenter !== "function") {
    return { opened: false, reason: "plugin_not_present" };
  }
  try {
    const result = await plugin.openNotificationCenter({
      theme: normalizeNativeThemeIdentifier(themeIdentifier),
    });
    return { opened: result?.opened === true };
  } catch (err) {
    return { opened: false, reason: "open_failed", error: err };
  }
}

/**
 * Consumes (and clears) any pending native-to-existing-route handoff set
 * by the native Notification Center (e.g. opening a message notification
 * routes to /chat/{chatId} in the existing React app). Returns "" when
 * there is nothing pending or the plugin isn't present. Best-effort.
 */
export async function consumeNativeNotificationHandoff() {
  const plugin = getNativeNotificationCenterPlugin();
  if (!plugin || typeof plugin.consumePendingHandoff !== "function") {
    return "";
  }
  try {
    const result = await plugin.consumePendingHandoff();
    return String(result?.path || "");
  } catch (err) {
    console.warn("consumeNativeNotificationHandoff failed:", err);
    return "";
  }
}

/**
 * Discards any pending native-to-existing-route handoff without navigating
 * to it. Reuses the existing consumePendingHandoff() call (which already
 * clears the native static field as a side effect of returning it) purely
 * for its clearing effect, discarding the returned path - called from
 * logoutNow() so a route queued for the account that is signing out can
 * never be delivered into a different account's session after a fresh
 * login on the same device. Best-effort; never throws.
 */
export async function clearPendingNativeNotificationHandoff() {
  try {
    await consumeNativeNotificationHandoff();
  } catch {
    // consumeNativeNotificationHandoff already swallows its own errors and
    // returns "" - this catch exists only so a future change to it can
    // never make logout itself throw.
  }
}

/**
 * Subscribe to the native "pendingHandoffChanged" event: fired as a
 * best-effort nudge whenever MainActivity has just queued a new
 * hand-off route (see NativeNotificationCenterPlugin.setPendingHandoffPath).
 * This is a timeliness signal only - the actual route is still fetched via
 * consumeNativeNotificationHandoff(), which remains the single source of
 * truth. Returns a handle with .remove() to unsubscribe; a no-op handle if
 * the plugin isn't present, so callers never need to branch.
 *
 * @param {() => void} callback
 */
export function addPendingHandoffChangeListener(callback) {
  const plugin = getNativeNotificationCenterPlugin();
  if (!plugin || typeof plugin.addListener !== "function") {
    return { remove: async () => {} };
  }

  return plugin.addListener("pendingHandoffChanged", () => {
    callback();
  });
}
